import React, { useEffect, useState } from "react";
import voteForArticle from "../lib/vote-for-article";
import styles from "../styles/articles-votes.module.css";
import { Article } from "../lib/article-model";
import { THCSessionDetails } from "../lib/hc-session";
import trackSessionEventArticleVote from "../lib/hc-session";
import { useRouter } from "next/router";
import useRefCallback from "use-ref-callback";
import FeedbackThumbIcon from "@atlas/uicorn/lib/Icon/FeedbackThumbIcon";
import t from "../texts/t";

export type ArticlesVotesProps = {
  className?: string;
  article: Article;
  hcSessionDetails?: THCSessionDetails;
  visibility: "always" | "no results" | "never";
  referer: string | null;
  language?: string;
};

export default function ArticlesVotes({
  className,
  article,
  hcSessionDetails,
  visibility,
  referer,
  language,
}: ArticlesVotesProps) {
  const router = useRouter();
  const pageUrl = router.asPath;

  const [processing, setProcessing] = useState(false);
  const [votes, setVotes] = useState({
    up: article.upVotes,
    down: article.downVotes,
  });

  const [voted, setVoted] = useState<{
    up: false | number;
    down: false | number;
  }>({ up: false, down: false });

  useEffect(() => {
    setVotes({
      up: article.upVotes,
      down: article.downVotes,
    });
    setVoted({
      up:
        parseInt(
          sessionStorage.getItem(`atlas/votes-up/${article.id}`) || ""
        ) || false,
      down:
        parseInt(
          sessionStorage.getItem(`atlas/votes-down/${article.id}`) || ""
        ) || false,
    });
  }, [article]);

  const applyVote = useRefCallback(
    (direction: "up" | "down", notify: boolean = false) => {
      setProcessing(true);
      const value = voted[direction] ? -1 : 1;
      setVotes((v) => ({ ...v, [direction]: v[direction] + value }));
      const now = Date.now();
      setVoted((v) => ({ ...v, [direction]: v[direction] ? false : now }));
      if (voted[direction]) {
        sessionStorage.removeItem(`atlas/votes-${direction}/${article.id}`);
      } else {
        sessionStorage.setItem(
          `atlas/votes-${direction}/${article.id}`,
          now.toString()
        );
      }
      return voteForArticle(
        article.id,
        direction,
        value,
        hcSessionDetails?.userId,
        notify
      )
        .then((article) => {
          setVotes({ up: article.upVotes, down: article.downVotes });
          setProcessing(false);
        })
        .then(() => {
          if (!hcSessionDetails) return;
          trackSessionEventArticleVote(
            "VOTED",
            hcSessionDetails,
            pageUrl,
            [article],
            [],
            { direction, referer }
          );
        });
    }
  );

  const vote = (direction: "up" | "down") => {
    const opposite = direction === "up" ? "down" : "up";
    applyVote(direction, true).then(() => {
      if (!voted[direction] && voted[opposite]) applyVote(opposite, false);
    });
  };

  const [votedMessageVisible, setVotedMessageVisible] = useState(false);
  useEffect(() => {
    const lastVoted = Math.max(voted.up || 0, voted.down || 0);
    const diff = Date.now() - lastVoted;
    if (diff > 10e3) return setVotedMessageVisible(false);

    setVotedMessageVisible(true);
    const timeout = setTimeout(
      () => setVotedMessageVisible(false),
      10e3 - diff
    );
    return () => clearTimeout(timeout);
  }, [voted, setVotedMessageVisible]);

  if (visibility === "never") return null;

  return (
    <div
      className={`${className || ""} ${styles.articleVotes}`}
      lang={language}
    >
      {votedMessageVisible ? (
        <span className={styles.articleVotesSuccessMessage}>
          {t("We appreciate your feedback!", language)}
        </span>
      ) : (
        <React.Fragment>
          <span>{t("Was this article helpful?", language)}</span>
          <span>
            <button
              className={`${styles.articleVotesButton} ${
                voted.up ? styles.articleVersionButtonChecked : ""
              } ${processing ? styles.articleVersionButtonDisabled : ""}`}
              type="button"
              onClick={() => vote("up")}
              title={t("Vote Up", language)}
              disabled={processing}
            >
              <FeedbackThumbIcon score={1} size={20} />{" "}
              <span className={styles.articleVotesButtonText}>
                {visibility === "always"
                  ? votes.up
                    ? "+" + votes.up
                    : "Yes"
                  : null}
              </span>
            </button>
            <button
              className={`${styles.articleVotesButton} ${
                voted.down ? styles.articleVersionButtonChecked : ""
              } ${processing ? styles.articleVersionButtonDisabled : ""}`}
              type="button"
              onClick={() => vote("down")}
              title={t("Vote Down", language)}
              disabled={processing}
            >
              <FeedbackThumbIcon size={20} />{" "}
              <span className={styles.articleVotesButtonText}>
                {visibility === "always"
                  ? votes.down
                    ? "+" + votes.down
                    : "No"
                  : null}
              </span>
            </button>
          </span>
        </React.Fragment>
      )}
    </div>
  );
}
